/**
 * Get the current app version
 * - For Tauri desktop/mobile apps: reads from tauri.conf.json
 * - For web apps: reads from package.json (injected at build time)
 */

// Extend ImportMeta interface to include Vite env variables
declare global {
  interface ImportMeta {
    env: {
      VITE_APP_VERSION?: string;
      [key: string]: string | undefined;
    };
  }
}

let cachedVersion: string | null = null;

export async function getAppVersion(): Promise<string> {
  // Return cached version if available
  if (cachedVersion) {
    return cachedVersion;
  }

  try {
    // Check if we're in a Tauri environment
    const isTauri = typeof window !== 'undefined' && 
                    '__TAURI_INTERNALS__' in window;

    if (isTauri) {
      // For Tauri apps, use the Tauri API
      try {
        const { getVersion } = await import('@tauri-apps/api/app');
        const version = await getVersion();
        cachedVersion = version;
        console.log('[Version] Detected Tauri app version:', version);
        return version;
      } catch (error) {
        console.warn('[Version] Failed to get Tauri version:', error);
      }
    }

    // For web apps or fallback, try to get from injected build config
    // This will be replaced by Vite during build
    const buildVersion = import.meta.env.VITE_APP_VERSION;
    if (buildVersion) {
      cachedVersion = buildVersion;
      console.log('[Version] Using build-injected version:', buildVersion);
      return buildVersion;
    }

    // Last resort: hardcoded fallback
    console.warn('[Version] Using fallback version 0.1.0');
    cachedVersion = '0.1.0';
    return cachedVersion;
  } catch (error) {
    console.error('[Version] Error getting app version:', error);
    return '0.1.0';
  }
}

/**
 * Synchronous version getter (uses cached value)
 * Call getAppVersion() first to populate the cache
 */
export function getCachedVersion(): string {
  return cachedVersion || '0.1.0';
}
